//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';
// import AssignmentIcon from '@material-ui/icons/Assignment';
// import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getUniqueKey } from '../../common/arrayHelper';
import { PLACEHOLDER_FIELD_JOB_TITLE } from '../../common/placeholderHelper';
import { getEmployerActiveJobsSuggestions } from '../../common/services/talentCenterServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdTextField from '../../common/elemental/AfitdTextField';
import JobListingBookmark from './JobListingBookmark';
import AfitdAutocompleteClearInput from '../../common/elemental/AfitdAutocompleteClearInput';
import AfitdButton from '../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
// import _fpSet from 'lodash/fp/set';
import _map from 'lodash/map';
import _findIndex from 'lodash/findIndex';
import _isNil from 'lodash/isNil';
import _slice from 'lodash/slice';
import _pick from 'lodash/pick';
import _omit from 'lodash/omit';
import _isEmpty from 'lodash/isEmpty';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    header: {
        padding: '20px',
    },
    headingSection: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    },
    popover: {
        padding: '10px',
        minWidth: '400px',
    },
    popHeading: {
        flex: 1,
        fontWeight: 700,
        fontSize: '16px',
        margin: '8px 0',
    },
    subHeading: {
        fontWeight: 'bold',
        padding: 10,
    },
    activeJobToSaveToContainer: {
        marginTop: 10,
        marginBottom: 10,
    },
    itemBox: {
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
            width: '0.4em',
        },
        '&::-webkit-scrollbar-track': {
            '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '5px',
        },
    },
    popItem: {
        display: 'flex',
        alignItems: 'center',
    },
    popItemMiddleSection: {
        flex: 1,
        padding: '5px',
        minWidth: '230px',
    },
    user: {
        fontSize: '16px',
        fontWeight: 'bold',
    },
    email: {
        fontSize: '14px',
    },
    popFooter: {
        flex: 1,
        textAlign: 'right',
    },
    cancelBtn: {
        textTransform: 'none',
        backgroundColor: '#F0F2F6',
        marginRight: 10,
    },
    saveBtn: {
        textTransform: 'none',
        // backgroundColor: '#FC821F',
        // color: '#fff',
        // '&:hover': {
        //     background: '#FC821F',
        // },
    },
    closeIcon: {
        cursor: 'pointer',
    },
    heading: { fontWeight: 'bold', flex: 1 },
    divStyles: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    bookmarkjob: {
        component: 'span',
    },
    searchedItems: {
        height: '180px',
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
            width: '0.4em',
        },
        '&::-webkit-scrollbar-track': {
            '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '5px',
        },
    },
    saveToJobs: {
        height: '180px',
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
            width: '0.4em',
        },
        '&::-webkit-scrollbar-track': {
            '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '5px',
        },
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        employerId,
        esToken,
        setEsToken,
        handleClose,
        handleClickSave,
        jobSeeker,
        activeJobToSaveToOptions,
        setActiveJobToSaveToOptions,
        handleChangeActiveJobToSaveTo,
        isSuggestionLoading,
        setIsSuggestionLoading,
        isDirty,
        // setIsDirty,
        saveLoading,
    } = params;

    const defns = {
        closeIcon: {
            onClick: handleClose,
        },
        cancelBtn: {
            onClick: handleClose,
        },
        activeJobToSaveTo: {
            options: activeJobToSaveToOptions,
            getOptionLabel: option => option?.jobTitle || option,
            // getOptionSelected: (option, value) => option?.jobTitle === value,
            placeholder: PLACEHOLDER_FIELD_JOB_TITLE,
            onChange: handleChangeActiveJobToSaveTo,
            onSuggestionsFetchRequested: async value => {
                const keywords = value ? value : undefined;
                setIsSuggestionLoading(true);
                const options = await getEmployerActiveJobsSuggestions({ keywords, employerId, esToken, setEsToken });
                setActiveJobToSaveToOptions(options);
                setIsSuggestionLoading(false);
            },
            // blurOnSelect: true,
            // clearOnBlur: true,
            loading: isSuggestionLoading,
            // disableCloseOnSelect: true,
            // inputValue: '',
        },
        subHeading: {
            variant: 'body1',
        },
        saveBtn: {
            color: 'primary',
            loading: saveLoading,
            disabled: !isDirty,
            onClick: handleClickSave,
        },
        jobListingBookmark: {
            variant: 'delete',
            jobSeeker,
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SaveJobSeekerToMyJobsPopover = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const { employerId, esToken, setEsToken } = useContext(AppContext);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { popoverOpen, setPopoverOpen, anchorEl, setAnchorEl, id, jobSeeker, onSave } = props;
    const savedToJobs = _get(jobSeeker, 'data.savedToJobs');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [activeJobToSaveToOptions, setActiveJobToSaveToOptions] = useState([]);
    const [saveToJobs, setSaveToJobs] = useState([]);
    const [isSuggestionLoading, setIsSuggestionLoading] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // load default job title suggestions if it has not been done so
    useEffect(() => {
        const loadInitialJobTitlesSuggestions = async () => {
            setIsSuggestionLoading(true);
            const options = await getEmployerActiveJobsSuggestions({ employerId, esToken, setEsToken });
            setActiveJobToSaveToOptions(options);
            setIsSuggestionLoading(false);
        };
        if (_isNil(activeJobToSaveToOptions) || _isEmpty(activeJobToSaveToOptions)) {
            loadInitialJobTitlesSuggestions();
        }
    }, [employerId, activeJobToSaveToOptions, setActiveJobToSaveToOptions, esToken, setEsToken]);

    // when popover is open, reset dirty flag and active jobs search result
    useEffect(() => {
        if (popoverOpen) {
            setIsDirty(false);
            setActiveJobToSaveToOptions([]);
            setSaveToJobs(savedToJobs || []);
        }
    }, [popoverOpen, savedToJobs]);

    // useEffect(() => {
    //     setSaveToJobs(savedToJobs || []);
    // }, [savedToJobs]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleClose = () => {
        setAnchorEl(null);
        setPopoverOpen(false);
    };

    const handleChangeActiveJobToSaveTo = (evt, job) => {
        // console.log('-------->', { job });
        if (job) {
            const jobId = job.uid;
            const saveToJobDto = { jobId, ..._pick(job, ['jobTitle', 'country', 'state', 'city']) };
            setSaveToJobs(prev => {
                const foundJobIdx = _findIndex(prev, item => item.jobId === jobId);
                if (foundJobIdx < 0) {
                    // job not already in the list, add it
                    return [{ ...saveToJobDto, isNew: true }, ...prev];
                } else {
                    // job already in the list, if it is "to be deleted", "un-delete" it and move it to the top of the list
                    const foundJob = prev[foundJobIdx];
                    if (foundJob.isDelete) {
                        return [..._omit(foundJob, ['isDelete']), ..._slice(prev, 0, foundJobIdx), ..._slice(prev, foundJobIdx + 1)];
                    } else {
                        // job already in the list, don't add it
                        return [...prev];
                    }
                }
            });
            setIsDirty(true);
        }
    };

    const handleClickDeleteButton = job => {
        const jobId = _get(job, 'jobId');

        setSaveToJobs(prev => {
            const foundJobIdx = _findIndex(prev, item => item.jobId === jobId);
            const foundJob = prev[foundJobIdx];
            if (foundJob.isNew) {
                // if it was newly added, it is not stored in the db yet, just remove the entry
                return [..._slice(prev, 0, foundJobIdx), ..._slice(prev, foundJobIdx + 1)];
            } else {
                // mark it 'isDelete' so it will be removed from db
                return [..._slice(prev, 0, foundJobIdx), { ...foundJob, isDelete: true }, ..._slice(prev, foundJobIdx + 1)];
            }
        });
        setIsDirty(true);
    };

    const handleClickSave = async () => {
        if (onSave) {
            setSaveLoading(true);
            await onSave(saveToJobs);
            setSaveLoading(false);
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        employerId,
        esToken,
        setEsToken,
        handleClose,
        handleClickSave,
        jobSeeker,
        activeJobToSaveToOptions,
        setActiveJobToSaveToOptions,
        handleChangeActiveJobToSaveTo,
        isSuggestionLoading,
        setIsSuggestionLoading,
        isDirty,
        setIsDirty,
        saveLoading,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <Popover
                id={id}
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <div {...defns.popover}>
                    <div {...defns.divStyles}>
                        <AfitdTypography {...defns.heading}>Save to Job listing(s)</AfitdTypography>
                        <Close {...defns.closeIcon} />
                    </div>
                    <div {...defns.activeJobToSaveToContainer}>
                        <AfitdAutocompleteClearInput {...defns.activeJobToSaveTo} />
                    </div>
                    <div {...defns.saveToJobs}>
                        {_map(saveToJobs, (job, idx) => {
                            return job.isDelete ? null : (
                                <JobListingBookmark {...defns.jobListingBookmark} job={job} onDeleteButtonClick={handleClickDeleteButton} key={getUniqueKey()} />
                            );
                        })}
                    </div>
                    <div {...defns.popFooter}>
                        <AfitdButton {...defns.cancelBtn}>Cancel</AfitdButton>
                        <AfitdButton {...defns.saveBtn}>Save</AfitdButton>
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default SaveJobSeekerToMyJobsPopover;
